/**
 * Real-document preview pipeline for the AssetLocker lightbox.
 *
 * Given an upload's Blob + resolved MIME type, decide whether it's one
 * of the "real document" formats the lightbox can render inline — PDF,
 * .docx, .xlsx, .pptx — and produce the payload the matching lightbox
 * pane needs:
 *
 *   - **PDF** — no parsing at all; the lightbox points an `<iframe>` at
 *     the existing object URL and lets the browser's native viewer do
 *     the work. We only verify the `%PDF-` magic here.
 *   - **.docx** — handed to `docx-preview`'s `renderAsync` at pane-mount
 *     time (it renders into a live DOM node, so the parse can't happen
 *     here). We pass the Blob through after verifying the zip magic.
 *   - **.xlsx** — parsed with `exceljs`; each worksheet becomes a capped
 *     grid of display strings ({@link SheetPreview}) the pane renders as
 *     an HTML table.
 *   - **.pptx** — unzipped with `jszip`; slide + notes XML is reduced to
 *     text lines per slide ({@link SlidePreview}). This is an honest
 *     text outline, not a fidelity render — the pane labels it as such.
 *
 * All three libraries are loaded via dynamic `import()` so none of them
 * lands in the main bundle — a user who never opens an Office preview
 * never downloads a parser.
 *
 * Graceful degradation is the contract: any classification miss, magic
 * mismatch, oversize input, or parse failure resolves to `null` and the
 * caller (`AssetLocker.openUpload`) falls through to its existing
 * text-sniff → binary dispatch. The magic checks matter for the seeded
 * placeholders that ship small Markdown bodies under a `.pdf`/`.docx`
 * MIME (see the sniff comment in `AssetLocker.tsx`) — they fail the
 * byte check here and still land on the inline text preview, while a
 * genuine Office zip never reaches the UTF-8 sniff at all.
 *
 * Legacy `.doc`/`.xls`/`.ppt` binaries are NOT parseable by these libs
 * (they're OLE compound files, not zips) — {@link classifyDocMime}
 * calls them out as `"office-legacy"` so the binary fallback can show a
 * clearer message ({@link binaryFallbackNote}) instead of the generic
 * "Inline preview not available".
 */

/** Refuse to parse anything above this — a runaway workbook/deck parse
 *  would freeze the click handler. PDFs are exempt (never parsed; the
 *  browser's viewer streams the object URL itself). */
export const DOC_PARSE_MAX_BYTES = 25 * 1024 * 1024;

/** Per-sheet render caps for .xlsx — enough for any briefing-sized
 *  table while keeping the DOM (rows × cols <td> nodes) bounded. The
 *  pane shows a truncation note when either axis is clipped. */
export const XLSX_MAX_ROWS = 500;
export const XLSX_MAX_COLS = 50;

export type DocMimeKind = "pdf" | "docx" | "xlsx" | "pptx" | "office-legacy";

const OOXML_MIME: Record<string, Exclude<DocMimeKind, "pdf" | "office-legacy">> = {
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "pptx",
};

const LEGACY_OFFICE_MIME = new Set([
  "application/msword",
  "application/vnd.ms-excel",
  "application/vnd.ms-powerpoint",
]);

/** Classify a MIME type into a document-preview kind, or `null` when
 *  it's not a document format this pipeline knows about. */
export function classifyDocMime(mime: string): DocMimeKind | null {
  if (mime === "application/pdf") return "pdf";
  const ooxml = OOXML_MIME[mime];
  if (ooxml) return ooxml;
  if (LEGACY_OFFICE_MIME.has(mime)) return "office-legacy";
  return null;
}

/** Clearer binary-fallback copy for the formats we knowingly can't
 *  parse; `undefined` keeps the lightbox's generic message. */
export function binaryFallbackNote(mime: string): string | undefined {
  if (classifyDocMime(mime) === "office-legacy") {
    return "Preview supports the modern .docx / .xlsx / .pptx formats — download to view.";
  }
  return undefined;
}

/** `%PDF-` somewhere in the first bytes (the spec allows up to 1 KiB of
 *  junk before the header, so we scan rather than prefix-match). */
export function hasPdfMagic(head: Uint8Array): boolean {
  const marker = [0x25, 0x50, 0x44, 0x46, 0x2d]; // "%PDF-"
  outer: for (let i = 0; i + marker.length <= head.length; i++) {
    for (let j = 0; j < marker.length; j++) {
      if (head[i + j] !== marker[j]) continue outer;
    }
    return true;
  }
  return false;
}

/** `PK\x03\x04` — every OOXML container is a plain zip. */
export function hasZipMagic(head: Uint8Array): boolean {
  return (
    head.length >= 4 &&
    head[0] === 0x50 &&
    head[1] === 0x4b &&
    head[2] === 0x03 &&
    head[3] === 0x04
  );
}

/* ─────────────────────────── .xlsx ─────────────────────────── */

/** One worksheet, reduced to display strings and capped to
 *  {@link XLSX_MAX_ROWS} × {@link XLSX_MAX_COLS}. */
export interface SheetPreview {
  name: string;
  /** Dense `rows × cols` grid of cell display text (`Cell.text`, so
   *  formulas show their cached result, not the formula). */
  rows: string[][];
  totalRows: number;
  totalCols: number;
  /** Either axis exceeded its cap — the pane shows a truncation note. */
  truncated: boolean;
}

/**
 * Parse an .xlsx into per-sheet display grids. Throws on any exceljs
 * parse failure or an empty workbook — callers treat a throw as "not
 * previewable" and fall back.
 */
export async function extractXlsxSheets(
  data: ArrayBuffer,
): Promise<SheetPreview[]> {
  const ExcelJS = await loadExcelJS();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(data);
  const sheets: SheetPreview[] = [];
  workbook.eachSheet((ws) => {
    const totalRows = ws.rowCount;
    const totalCols = ws.columnCount;
    const rowN = Math.min(totalRows, XLSX_MAX_ROWS);
    const colN = Math.min(totalCols, XLSX_MAX_COLS);
    const rows: string[][] = [];
    for (let r = 1; r <= rowN; r++) {
      const row = ws.getRow(r);
      const cells: string[] = [];
      for (let c = 1; c <= colN; c++) {
        // `Cell.text` is exceljs's formatted display string; coerce
        // defensively — rich-text edge cases have returned objects.
        cells.push(String(row.getCell(c).text ?? ""));
      }
      rows.push(cells);
    }
    sheets.push({
      name: ws.name,
      rows,
      totalRows,
      totalCols,
      truncated: totalRows > XLSX_MAX_ROWS || totalCols > XLSX_MAX_COLS,
    });
  });
  if (sheets.length === 0) throw new Error("workbook has no worksheets");
  return sheets;
}

/** Dynamic-import exceljs with UMD interop — depending on the bundler's
 *  CJS handling the module namespace is either the library itself or a
 *  `{ default }` wrapper around it. */
async function loadExcelJS(): Promise<typeof import("exceljs")> {
  const mod = await import("exceljs");
  const wrapped = (mod as { default?: typeof import("exceljs") }).default;
  return wrapped && "Workbook" in wrapped ? wrapped : mod;
}

/* ─────────────────────────── .pptx ─────────────────────────── */

/** One slide's text outline (+ speaker notes when present). */
export interface SlidePreview {
  /** 1-based slide number from the part name (`slideN.xml`). */
  number: number;
  /** Non-empty paragraph texts, in document order. */
  lines: string[];
  /** Speaker-notes paragraph texts; empty when the slide has none. */
  notes: string[];
}

/** DrawingML namespace — every text run in slide XML is an `a:t`. */
const DRAWING_NS = "http://schemas.openxmlformats.org/drawingml/2006/main";

/**
 * Reduce one slide (or notes-slide) XML part to its paragraph texts:
 * each DrawingML `a:p` paragraph becomes one line (its `a:t` runs
 * concatenated); empty paragraphs are dropped. Exported for the unit
 * test — it's the pure core of the pptx path.
 */
export function extractTextLines(xml: string): string[] {
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  // Namespace-aware lookup first; fall back to the literal prefixed
  // name for parsers that don't index by namespace.
  let paragraphs: ArrayLike<Element> = doc.getElementsByTagNameNS(
    DRAWING_NS,
    "p",
  );
  if (paragraphs.length === 0) {
    paragraphs = doc.getElementsByTagName("a:p");
  }
  const lines: string[] = [];
  for (let i = 0; i < paragraphs.length; i++) {
    const p = paragraphs[i]!;
    let runs: ArrayLike<Element> = p.getElementsByTagNameNS(DRAWING_NS, "t");
    if (runs.length === 0) runs = p.getElementsByTagName("a:t");
    let text = "";
    for (let j = 0; j < runs.length; j++) {
      text += runs[j]!.textContent ?? "";
    }
    const trimmed = text.trim();
    if (trimmed) lines.push(trimmed);
  }
  return lines;
}

/**
 * Unzip a .pptx and build the per-slide text outline. Slides are read
 * from `ppt/slides/slideN.xml`, sorted numerically (string sort would
 * put slide10 before slide2). Notes are matched by number
 * (`ppt/notesSlides/notesSlideN.xml`) — strictly the mapping lives in
 * the rels part, but every mainstream producer numbers them 1:1 and a
 * mismatch only degrades to "no notes", never to wrong text. Throws on
 * a zip without slides — callers treat a throw as "not previewable".
 */
export async function extractPptxOutline(
  data: ArrayBuffer,
): Promise<SlidePreview[]> {
  const { default: JSZip } = await import("jszip");
  const zip = await JSZip.loadAsync(data);
  const slideEntries = Object.keys(zip.files)
    .map((path) => {
      const m = /^ppt\/slides\/slide(\d+)\.xml$/.exec(path);
      return m ? { path, number: Number(m[1]) } : null;
    })
    .filter((e): e is { path: string; number: number } => e !== null)
    .sort((a, b) => a.number - b.number);
  if (slideEntries.length === 0) {
    throw new Error("archive contains no ppt/slides/slideN.xml parts");
  }
  const slides: SlidePreview[] = [];
  for (const { path, number } of slideEntries) {
    const xml = await zip.file(path)!.async("string");
    const notesFile = zip.file(`ppt/notesSlides/notesSlide${number}.xml`);
    slides.push({
      number,
      lines: extractTextLines(xml),
      notes: notesFile ? extractTextLines(await notesFile.async("string")) : [],
    });
  }
  return slides;
}

/* ─────────────────────────── .docx ─────────────────────────── */

/**
 * Render a .docx into a live container via docx-preview. Called by the
 * lightbox's docx pane at mount (the library renders straight into the
 * DOM — there's no parse-to-data step to do up front). Rejects on parse
 * failure; the pane catches and swaps to the download fallback.
 */
export async function renderDocxInto(
  blob: Blob,
  container: HTMLElement,
): Promise<void> {
  const { renderAsync } = await import("docx-preview");
  await renderAsync(blob, container, undefined, {
    // One continuous flow instead of paginated canvases — the pane is
    // a scroll area, and honoring the page height just wastes space.
    ignoreHeight: true,
    ignoreLastRenderedPageBreak: true,
  });
}

/* ─────────────────────────── Dispatch ─────────────────────────── */

/** What `buildDocPreview` hands back — mirrors the lightbox's new
 *  `PreviewState` kinds minus the common upload/objectUrl/mime fields. */
export type DocPreviewPayload =
  | { kind: "pdf" }
  | { kind: "docx"; docBlob: Blob }
  | { kind: "xlsx"; sheets: SheetPreview[] }
  | { kind: "pptx"; slides: SlidePreview[] };

/**
 * The single entry point `AssetLocker.openUpload` calls before its text
 * sniff. Returns the preview payload for a genuine PDF/.docx/.xlsx/.pptx,
 * or `null` for everything else — unknown MIME, legacy Office, oversize
 * input, magic-byte mismatch (the seeded markdown-with-office-MIME
 * placeholders), or a parse failure. `null` always means "use the
 * existing sniff → binary path", never a broken pane.
 */
export async function buildDocPreview(
  blob: Blob,
  mime: string,
): Promise<DocPreviewPayload | null> {
  const kind = classifyDocMime(mime);
  if (!kind || kind === "office-legacy") return null;
  try {
    const head = new Uint8Array(await blob.slice(0, 1024).arrayBuffer());
    if (kind === "pdf") {
      return hasPdfMagic(head) ? { kind: "pdf" } : null;
    }
    if (!hasZipMagic(head)) return null;
    if (blob.size > DOC_PARSE_MAX_BYTES) return null;
    if (kind === "docx") return { kind: "docx", docBlob: blob };
    const data = await blob.arrayBuffer();
    if (kind === "xlsx") {
      return { kind: "xlsx", sheets: await extractXlsxSheets(data) };
    }
    return { kind: "pptx", slides: await extractPptxOutline(data) };
  } catch {
    // Read or parse failure — degrade to the caller's fallback chain.
    return null;
  }
}
